import React, { useState, useEffect } from 'react';
import { warehousesAPI, shelvesAPI, binsAPI, traysAPI, Warehouse, Shelf } from '../../services/api';
import {
  XMarkIcon,
  PlusIcon,
  BuildingOfficeIcon,
  CubeIcon,
  ArchiveBoxIcon,
  RectangleStackIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

interface WarehouseDetailProps {
  warehouseId: number;
  onClose: () => void;
}

const WarehouseDetail: React.FC<WarehouseDetailProps> = ({ warehouseId, onClose }) => {
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [setupStep, setSetupStep] = useState(1);
  const [setupData, setSetupData] = useState({
    numShelves: '',
    numBinsPerShelf: '',
    numTraysPerBin: '',
    shelfPrefix: 'S',
    binPrefix: 'B',
    trayPrefix: 'T',
    binCapacity: '100',
    trayCapacity: '50',
  });
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState('');

  useEffect(() => {
    fetchWarehouse();
  }, [warehouseId]);

  const fetchWarehouse = async () => {
    try {
      setLoading(true);
      const data = await warehousesAPI.getOne(warehouseId);
      setWarehouse(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch warehouse details');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupWarehouse = async () => {
    setSetupLoading(true);
    setError('');
    setSetupSuccess('');

    try {
      const numShelves = parseInt(setupData.numShelves);
      const numBinsPerShelf = parseInt(setupData.numBinsPerShelf);
      const numTraysPerBin = parseInt(setupData.numTraysPerBin);

      // Create shelves
      for (let s = 1; s <= numShelves; s++) {
        const shelf = await shelvesAPI.create({
          warehouseId,
          code: `${setupData.shelfPrefix}${s}`,
          description: `Shelf ${s}`,
        });

        // Create bins for each shelf
        for (let b = 1; b <= numBinsPerShelf; b++) {
          const bin = await binsAPI.create({
            shelfId: shelf.id,
            code: `${setupData.binPrefix}${b}`,
            capacity: parseInt(setupData.binCapacity),
          });

          // Create trays for each bin
          for (let t = 1; t <= numTraysPerBin; t++) {
            await traysAPI.create({
              binId: bin.id,
              code: `${setupData.trayPrefix}${t}`,
              capacity: parseInt(setupData.trayCapacity),
            });
          }
        }
      }

      setSetupSuccess(
        `Successfully created ${numShelves} shelves, ${numShelves * numBinsPerShelf} bins, and ${numShelves * numBinsPerShelf * numTraysPerBin} trays!`
      );
      setTimeout(() => {
        setShowSetupWizard(false);
        fetchWarehouse();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to setup warehouse');
    } finally {
      setSetupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!warehouse) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-neutral-900">{warehouse.name}</h3>
              <p className="text-sm text-neutral-500">Warehouse Code: {warehouse.code}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {setupSuccess && (
            <div className="mb-4 bg-success-50 border border-success-200 text-success-600 px-4 py-3 rounded-lg">
              {setupSuccess}
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-100 text-sm">Shelves</p>
                  <p className="text-2xl font-bold mt-1">{warehouse.shelves?.length || 0}</p>
                </div>
                <CubeIcon className="h-10 w-10 text-primary-200" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-success-500 to-success-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-success-100 text-sm">Total Bins</p>
                  <p className="text-2xl font-bold mt-1">
                    {warehouse.shelves?.reduce((sum, shelf) => sum + (shelf._count?.bins || 0), 0) || 0}
                  </p>
                </div>
                <ArchiveBoxIcon className="h-10 w-10 text-success-200" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-secondary-100 text-sm">Capacity</p>
                  <p className="text-2xl font-bold mt-1">{warehouse.capacity || 'N/A'}</p>
                </div>
                <RectangleStackIcon className="h-10 w-10 text-secondary-200" />
              </div>
            </div>
            <div className="bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-warning-100 text-sm">Status</p>
                  <p className="text-lg font-semibold mt-1">{warehouse.isActive ? 'Active' : 'Inactive'}</p>
                </div>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${warehouse.isActive ? 'bg-success-400' : 'bg-neutral-400'}`}>
                  <div className="h-5 w-5 rounded-full bg-white"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Warehouse Info */}
          <div className="bg-neutral-50 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold text-neutral-900 mb-4">Warehouse Information</h4>
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {warehouse.address && (
                <div>
                  <dt className="text-sm font-medium text-neutral-500">Address</dt>
                  <dd className="mt-1 text-sm text-neutral-900">{warehouse.address}</dd>
                </div>
              )}
              {warehouse.city && (
                <div>
                  <dt className="text-sm font-medium text-neutral-500">City</dt>
                  <dd className="mt-1 text-sm text-neutral-900">{warehouse.city}</dd>
                </div>
              )}
              {warehouse.state && (
                <div>
                  <dt className="text-sm font-medium text-neutral-500">State</dt>
                  <dd className="mt-1 text-sm text-neutral-900">{warehouse.state}</dd>
                </div>
              )}
              {warehouse.country && (
                <div>
                  <dt className="text-sm font-medium text-neutral-500">Country</dt>
                  <dd className="mt-1 text-sm text-neutral-900">{warehouse.country}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Setup Wizard Button */}
          {(!warehouse.shelves || warehouse.shelves.length === 0) && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6">
              <div className="flex items-start">
                <Cog6ToothIcon className="h-6 w-6 text-primary-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-primary-900 mb-2">Setup Warehouse Structure</h4>
                  <p className="text-sm text-primary-700 mb-4">
                    This warehouse doesn't have any shelves yet. Use the setup wizard to quickly create shelves, bins, and trays.
                  </p>
                  <button
                    onClick={() => setShowSetupWizard(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    <Cog6ToothIcon className="h-5 w-5 mr-2" />
                    Start Setup Wizard
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Shelves List */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-neutral-900">
                Shelves ({warehouse.shelves?.length || 0})
              </h4>
              {warehouse.shelves && warehouse.shelves.length > 0 && (
                <button
                  onClick={() => setShowSetupWizard(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add More
                </button>
              )}
            </div>
            {warehouse.shelves && warehouse.shelves.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {warehouse.shelves.map((shelf) => (
                  <div key={shelf.id} className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center">
                        <div className="p-2 bg-primary-100 rounded-lg mr-3">
                          <CubeIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <h5 className="font-semibold text-neutral-900">Shelf {shelf.code}</h5>
                          {shelf.description && (
                            <p className="text-xs text-neutral-500 mt-0.5">{shelf.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-500">Bins:</span>
                        <span className="font-medium text-neutral-900">{shelf._count?.bins || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-neutral-50 rounded-lg">
                <CubeIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900">No shelves</h3>
                <p className="mt-1 text-sm text-neutral-500">Get started by setting up your warehouse structure.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-neutral-50 border-t border-neutral-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Setup Wizard Modal */}
      {showSetupWizard && (
        <div className="fixed inset-0 bg-neutral-900 bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-neutral-900">Warehouse Setup Wizard</h3>
                <button onClick={() => setShowSetupWizard(false)} className="text-neutral-400 hover:text-neutral-500">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`flex items-center ${setupStep >= 1 ? 'text-primary-600' : 'text-neutral-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${setupStep >= 1 ? 'bg-primary-600 text-white' : 'bg-neutral-200'}`}>
                      1
                    </div>
                    <span className="ml-2 text-sm font-medium">Configuration</span>
                  </div>
                  <div className="flex-1 h-1 mx-4 bg-neutral-200">
                    <div className={`h-full ${setupStep >= 2 ? 'bg-primary-600' : 'bg-neutral-200'}`} style={{ width: setupStep >= 2 ? '100%' : '0%' }}></div>
                  </div>
                  <div className={`flex items-center ${setupStep >= 2 ? 'text-primary-600' : 'text-neutral-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${setupStep >= 2 ? 'bg-primary-600 text-white' : 'bg-neutral-200'}`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Confirm</span>
                  </div>
                </div>
              </div>

              {setupStep === 1 && (
                <div className="space-y-4">
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-primary-800">
                      Configure the structure of your warehouse. This will automatically create shelves, bins, and trays based on your specifications.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Number of Shelves *</label>
                      <input
                        type="number"
                        min="1"
                        value={setupData.numShelves}
                        onChange={(e) => setSetupData({ ...setupData, numShelves: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Bins per Shelf *</label>
                      <input
                        type="number"
                        min="1"
                        value={setupData.numBinsPerShelf}
                        onChange={(e) => setSetupData({ ...setupData, numBinsPerShelf: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="e.g., 5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Trays per Bin *</label>
                      <input
                        type="number"
                        min="1"
                        value={setupData.numTraysPerBin}
                        onChange={(e) => setSetupData({ ...setupData, numTraysPerBin: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        placeholder="e.g., 4"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Shelf Prefix</label>
                      <input
                        type="text"
                        value={setupData.shelfPrefix}
                        onChange={(e) => setSetupData({ ...setupData, shelfPrefix: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Bin Prefix</label>
                      <input
                        type="text"
                        value={setupData.binPrefix}
                        onChange={(e) => setSetupData({ ...setupData, binPrefix: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Tray Prefix</label>
                      <input
                        type="text"
                        value={setupData.trayPrefix}
                        onChange={(e) => setSetupData({ ...setupData, trayPrefix: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Bin Capacity (units)</label>
                      <input
                        type="number"
                        min="1"
                        value={setupData.binCapacity}
                        onChange={(e) => setSetupData({ ...setupData, binCapacity: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Tray Capacity (units)</label>
                      <input
                        type="number"
                        min="1"
                        value={setupData.trayCapacity}
                        onChange={(e) => setSetupData({ ...setupData, trayCapacity: e.target.value })}
                        className="block w-full rounded-md border-neutral-300 bg-white shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {setupStep === 2 && (
                <div className="space-y-4">
                  <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-success-900 mb-2">Setup Summary</h4>
                    <div className="text-sm text-success-800 space-y-1">
                      <p>• <strong>{setupData.numShelves}</strong> shelves will be created</p>
                      <p>• <strong>{parseInt(setupData.numShelves || '0') * parseInt(setupData.numBinsPerShelf || '0')}</strong> bins will be created ({setupData.numBinsPerShelf} per shelf)</p>
                      <p>• <strong>{parseInt(setupData.numShelves || '0') * parseInt(setupData.numBinsPerShelf || '0') * parseInt(setupData.numTraysPerBin || '0')}</strong> trays will be created ({setupData.numTraysPerBin} per bin)</p>
                      <p className="mt-2">Naming convention: {setupData.shelfPrefix}1-{setupData.binPrefix}1-{setupData.trayPrefix}1</p>
                    </div>
                  </div>

                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                    <p className="text-sm text-warning-800">
                      <strong>Note:</strong> This process may take a few moments depending on the number of items being created. Please do not close this window.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200 flex justify-between">
              <button
                onClick={() => {
                  if (setupStep === 1) {
                    setShowSetupWizard(false);
                  } else {
                    setSetupStep(1);
                  }
                }}
                disabled={setupLoading}
                className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-50"
              >
                {setupStep === 1 ? 'Cancel' : 'Back'}
              </button>
              <button
                onClick={() => {
                  if (setupStep === 1) {
                    setSetupStep(2);
                  } else {
                    handleSetupWarehouse();
                  }
                }}
                disabled={setupLoading || !setupData.numShelves || !setupData.numBinsPerShelf || !setupData.numTraysPerBin}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {setupLoading ? 'Creating...' : setupStep === 1 ? 'Next' : 'Create Structure'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseDetail;

